import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { MOCK_PRINTERS, mockCrud, loadFromStorage } from '@/services/mockData'
import { TIER_FEATURES } from '@/services/stripe'
import { getNextTierName } from '@/composables/useTierLimits'
import type { Printer, PrinterFormData, PrinterStatus, PrinterType, PrinterTelemetry, MaterialSystem, PrinterSlotAssignment } from '@/types'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { relay } from '@/services/relay'

/** Generate a unique printer code: phq_printer_XXXXXXXXXX (10 random alphanumeric chars) */
function generatePrinterCode(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let code = 'phq_printer_'
  for (let i = 0; i < 10; i++) {
    code += chars[Math.floor(Math.random() * chars.length)]
  }
  return code
}

const STORAGE_KEY = 'printers'

/**
 * Live printer telemetry: the printer_telemetry snapshot, its Realtime channel and
 * the Bridge relay. Switched off because it only filled Supabase with errors; set
 * to true to bring it back.
 */
export const PRINTER_TELEMETRY = false

export const usePrinterStore = defineStore('printer', () => {
  const printers = ref<Printer[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const _loaded = ref(false)
  const _contextKey = ref<string>('personal')

  // Real-time telemetry state
  const telemetry = ref<Map<string, PrinterTelemetry>>(new Map())
  let telemetryChannel: RealtimeChannel | null = null
  let relayCleanup: (() => void) | null = null

  const activePrinters = computed(() => printers.value.filter(p => p.status !== 'offline'))
  const printingPrinters = computed(() => printers.value.filter(p => p.status === 'printing'))
  const idlePrinters = computed(() => printers.value.filter(p => p.status === 'idle'))
  const printersNeedingMaintenance = computed(() => printers.value.filter(p => p.status === 'maintenance'))

  const printersByType = computed(() => {
    const grouped: Record<PrinterType, Printer[]> = {} as Record<PrinterType, Printer[]>
    printers.value.forEach(p => {
      if (!grouped[p.type]) grouped[p.type] = []
      grouped[p.type].push(p)
    })
    return grouped
  })

  const totalPrintTime = computed(() => printers.value.reduce((sum, p) => sum + p.total_print_time, 0))
  const totalPrintCount = computed(() => printers.value.reduce((sum, p) => sum + p.total_prints, 0))
  const getById = (id: string) => printers.value.find(p => p.id === id)

  const fetchPrinters = async (force = false, { silent = false, teamId }: { silent?: boolean; teamId?: string } = {}) => {
    const authStore = useAuthStore()
    if (!authStore.userId) {
      console.warn('[printerStore] fetchPrinters called without userId')
      return
    }
    const newContext = teamId || 'personal'
    if (newContext !== _contextKey.value) {
      _loaded.value = false
      _contextKey.value = newContext
    }

    if (_loaded.value && !force) return

    if (!silent) {
      loading.value = true
      error.value = null
    }

    try {
      if (authStore.isDemoMode) {
        await new Promise(resolve => setTimeout(resolve, 200))
        printers.value = loadFromStorage(STORAGE_KEY, MOCK_PRINTERS)
      } else {
        const { supabase } = await import('@/services/supabase')
        // Apply team or personal scope
        let query = supabase.from('printers').select('*')
        if (teamId) {
          query = query.eq('team_id', teamId)
        } else {
          query = query.is('team_id', null).eq('user_id', authStore.userId)
        }
        const { data, error: fetchError } = await query.order('created_at', { ascending: false })

        if (fetchError) throw fetchError
        printers.value = data as Printer[]
      }
      _loaded.value = true
    } catch (err) {
      if (!silent) error.value = err instanceof Error ? err.message : 'Failed to fetch printers'
    } finally {
      if (!silent) loading.value = false
    }
  }

  const clearData = () => {
    printers.value = []
    telemetry.value = new Map()
    _loaded.value = false
  }

  const createPrinter = async (printerData: PrinterFormData, teamId?: string) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    if (!teamId) {
      // Tier limit check (personal only)
      const tierConfig = TIER_FEATURES[authStore.subscriptionTier]
      const limit = tierConfig.printers
      if (limit !== -1 && printers.value.length >= limit) {
        const nextTier = getNextTierName(authStore.subscriptionTier)
        const upgradeHint = nextTier ? ` Upgrade to ${nextTier} for more.` : ''
        return { success: false, error: `You've reached the ${tierConfig.name} plan limit of ${limit} printers.${upgradeHint}` }
      }
    }

    loading.value = true
    try {
      const printer_code = generatePrinterCode()

      if (authStore.isDemoMode) {
        const newPrinter = mockCrud.create(STORAGE_KEY, {
          ...printerData,
          printer_code,
          user_id: authStore.userId,
          total_print_time: 0,
          total_prints: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as Printer, printers.value)
        printers.value.unshift(newPrinter)
        return { success: true, data: newPrinter }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: insertError } = await supabase
          .from('printers')
          .insert({ ...(printerData), printer_code, user_id: authStore.userId, total_print_time: 0, total_prints: 0, ...(teamId ? { team_id: teamId } : {}) })
          .select()
          .single()

        if (insertError) throw insertError
        printers.value.unshift(data as Printer)
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create printer'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updatePrinter = async (id: string, updates: Partial<PrinterFormData>) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        const updated = mockCrud.update(STORAGE_KEY, id, updates, printers.value)
        if (updated) {
          const index = printers.value.findIndex(p => p.id === id)
          if (index !== -1) printers.value[index] = updated
        }
        return { success: true, data: updated }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: updateError } = await supabase
          .from('printers')
          .update({ ...(updates), updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single()

        if (updateError) throw updateError
        const index = printers.value.findIndex(p => p.id === id)
        if (index !== -1) printers.value[index] = data as Printer
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update printer'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const deletePrinter = async (id: string) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        mockCrud.delete(STORAGE_KEY, id, printers.value)
        printers.value = printers.value.filter(p => p.id !== id)
        return { success: true }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { error: deleteError } = await supabase.from('printers').delete().eq('id', id)
        if (deleteError) throw deleteError
        printers.value = printers.value.filter(p => p.id !== id)
        return { success: true }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete printer'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updateStatus = async (id: string, status: PrinterStatus) => updatePrinter(id, { status })

  const incrementPrintStats = async (id: string, printTime: number) => {
    const printer = getById(id)
    if (!printer) return { success: false, error: 'Printer not found' }
    return updatePrinter(id, {
      total_print_time: printer.total_print_time + printTime,
      total_prints: printer.total_prints + 1
    } as any)
  }

  const recordMaintenance = async (id: string) => {
    return updatePrinter(id, { last_maintenance_date: new Date().toISOString(), status: 'idle' } as any)
  }

  // ─── Print Setting Definitions (per-printer, from Bridge) ────────────

  interface PrintSettingDefinition {
    key: string
    label: string
    type: 'boolean' | 'number' | 'select'
    default: boolean | number | string
    options?: { label: string; value: string | number }[]
    onValue?: boolean | number | string
    offValue?: boolean | number | string
    min?: number
    max?: number
    description?: string
  }

  const printSettingDefs = ref<Map<string, PrintSettingDefinition[]>>(new Map())

  const getPrintSettingDefinitions = (printerId: string): PrintSettingDefinition[] => {
    return printSettingDefs.value.get(printerId) || []
  }

  // ─── Material System & Slot Assignments ──────────────────────────────

  const slotAssignments = ref<Map<string, PrinterSlotAssignment[]>>(new Map())
  const SLOT_STORAGE_KEY = 'printer_slot_assignments'

  const getSlotAssignments = (printerId: string): PrinterSlotAssignment[] => {
    return slotAssignments.value.get(printerId) || []
  }

  const fetchSlotAssignments = async (printerId: string) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return

    try {
      if (authStore.isDemoMode) {
        const all: PrinterSlotAssignment[] = loadFromStorage(SLOT_STORAGE_KEY, []) as any[]
        const forPrinter = all.filter(s => s.printer_id === printerId)
          .sort((a, b) => a.slot_index - b.slot_index)
        const newMap = new Map(slotAssignments.value)
        newMap.set(printerId, forPrinter)
        slotAssignments.value = newMap
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data } = await supabase
          .from('printer_slot_assignments')
          .select('*')
          .eq('printer_id', printerId)
          .order('slot_index')

        const newMap = new Map(slotAssignments.value)
        newMap.set(printerId, (data || []) as PrinterSlotAssignment[])
        slotAssignments.value = newMap
      }
    } catch (err) {
      console.error('Failed to fetch slot assignments:', err)
    }
  }

  /** Batch-fetch slot assignments for all printers in one query (avoids N+1 on scheduler) */
  const fetchAllSlotAssignments = async () => {
    const authStore = useAuthStore()
    if (!authStore.userId) return

    try {
      if (authStore.isDemoMode) {
        const all: PrinterSlotAssignment[] = loadFromStorage(SLOT_STORAGE_KEY, []) as any[]
        const newMap = new Map<string, PrinterSlotAssignment[]>()
        for (const s of all) {
          const list = newMap.get(s.printer_id) || []
          list.push(s)
          newMap.set(s.printer_id, list)
        }
        for (const list of newMap.values()) list.sort((a, b) => a.slot_index - b.slot_index)
        slotAssignments.value = newMap
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data } = await supabase
          .from('printer_slot_assignments')
          .select('*')
          .order('slot_index')

        const newMap = new Map<string, PrinterSlotAssignment[]>()
        for (const row of (data || []) as PrinterSlotAssignment[]) {
          const list = newMap.get(row.printer_id) || []
          list.push(row)
          newMap.set(row.printer_id, list)
        }
        slotAssignments.value = newMap
      }
    } catch (err) {
      console.error('Failed to fetch all slot assignments:', err)
    }
  }

  const updateMaterialSystem = async (printerId: string, system: MaterialSystem | null) => {
    const result = await updatePrinter(printerId, { material_system: system } as any)
    if (result.success && system) {
      await initializeSlots(printerId, system.slot_count)
    } else if (result.success && !system) {
      await clearSlotAssignments(printerId)
    }
    return result
  }

  const initializeSlots = async (printerId: string, slotCount: number) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return

    const existing = getSlotAssignments(printerId)
    // Only create slots that don't exist yet, remove extras
    const toKeep = existing.filter(s => s.slot_index < slotCount)
    const existingIndices = new Set(toKeep.map(s => s.slot_index))
    const toCreate: number[] = []
    for (let i = 0; i < slotCount; i++) {
      if (!existingIndices.has(i)) toCreate.push(i)
    }
    const toRemove = existing.filter(s => s.slot_index >= slotCount)

    try {
      if (authStore.isDemoMode) {
        const all: any[] = loadFromStorage(SLOT_STORAGE_KEY, []) as any[]
        // Remove extras
        const removeIds = new Set(toRemove.map(s => s.id))
        const filtered = all.filter(s => !removeIds.has(s.id))
        // Add new
        for (const idx of toCreate) {
          filtered.push({
            id: crypto.randomUUID(),
            user_id: authStore.userId,
            printer_id: printerId,
            slot_index: idx,
            spool_id: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
        }
        const { saveToStorage } = await import('@/services/mockData')
        saveToStorage(SLOT_STORAGE_KEY, filtered)
        await fetchSlotAssignments(printerId)
      } else {
        const { supabase } = await import('@/services/supabase')
        // Remove excess slots
        if (toRemove.length > 0) {
          await supabase
            .from('printer_slot_assignments')
            .delete()
            .eq('printer_id', printerId)
            .gte('slot_index', slotCount)
        }
        // Insert missing slots
        if (toCreate.length > 0) {
          await supabase
            .from('printer_slot_assignments')
            .insert(toCreate.map(idx => ({
              user_id: authStore.userId,
              printer_id: printerId,
              slot_index: idx,
              spool_id: null,
            })))
        }
        await fetchSlotAssignments(printerId)
      }
    } catch (err) {
      console.error('Failed to initialize slots:', err)
    }
  }

  const assignSpoolToSlot = async (printerId: string, slotIndex: number, spoolId: string) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false }

    try {
      if (authStore.isDemoMode) {
        const all: any[] = loadFromStorage(SLOT_STORAGE_KEY, []) as any[]
        const idx = all.findIndex(s => s.printer_id === printerId && s.slot_index === slotIndex)
        if (idx !== -1) {
          all[idx].spool_id = spoolId
          all[idx].updated_at = new Date().toISOString()
        }
        const { saveToStorage } = await import('@/services/mockData')
        saveToStorage(SLOT_STORAGE_KEY, all)
        await fetchSlotAssignments(printerId)
      } else {
        const { supabase } = await import('@/services/supabase')
        await supabase
          .from('printer_slot_assignments')
          .update({ spool_id: spoolId, updated_at: new Date().toISOString() })
          .eq('printer_id', printerId)
          .eq('slot_index', slotIndex)
        await fetchSlotAssignments(printerId)
      }
      return { success: true }
    } catch (err) {
      console.error('Failed to assign spool:', err)
      return { success: false }
    }
  }

  const removeSpoolFromSlot = async (printerId: string, slotIndex: number) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false }

    try {
      if (authStore.isDemoMode) {
        const all: any[] = loadFromStorage(SLOT_STORAGE_KEY, []) as any[]
        const idx = all.findIndex(s => s.printer_id === printerId && s.slot_index === slotIndex)
        if (idx !== -1) {
          all[idx].spool_id = null
          all[idx].updated_at = new Date().toISOString()
        }
        const { saveToStorage } = await import('@/services/mockData')
        saveToStorage(SLOT_STORAGE_KEY, all)
        await fetchSlotAssignments(printerId)
      } else {
        const { supabase } = await import('@/services/supabase')
        await supabase
          .from('printer_slot_assignments')
          .update({ spool_id: null, updated_at: new Date().toISOString() })
          .eq('printer_id', printerId)
          .eq('slot_index', slotIndex)
        await fetchSlotAssignments(printerId)
      }
      return { success: true }
    } catch (err) {
      console.error('Failed to remove spool:', err)
      return { success: false }
    }
  }

  const clearSlotAssignments = async (printerId: string) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return

    try {
      if (authStore.isDemoMode) {
        const all: any[] = loadFromStorage(SLOT_STORAGE_KEY, []) as any[]
        const filtered = all.filter(s => s.printer_id !== printerId)
        const { saveToStorage } = await import('@/services/mockData')
        saveToStorage(SLOT_STORAGE_KEY, filtered)
      } else {
        const { supabase } = await import('@/services/supabase')
        await supabase
          .from('printer_slot_assignments')
          .delete()
          .eq('printer_id', printerId)
      }
      const newMap = new Map(slotAssignments.value)
      newMap.delete(printerId)
      slotAssignments.value = newMap
    } catch (err) {
      console.error('Failed to clear slot assignments:', err)
    }
  }

  // ─── Real-time Telemetry ─────────────────────────────────────────────

  const getTelemetry = (printerId: string): PrinterTelemetry | undefined => {
    return telemetry.value.get(printerId)
  }

  /** Fetch initial telemetry for all printers, then subscribe to real-time updates */
  const subscribeTelemetry = async () => {
    if (!PRINTER_TELEMETRY) return
    const authStore = useAuthStore()
    if (!authStore.userId || authStore.isDemoMode) return

    // Tear down any existing channel before re-subscribing
    // (handles stale WebSocket after tab was backgrounded)
    if (telemetryChannel) {
      const { supabase: sb } = await import('@/services/supabase')
      sb.removeChannel(telemetryChannel)
      telemetryChannel = null
    }

    const { supabase } = await import('@/services/supabase')

    // Fetch current telemetry snapshot
    const { data } = await supabase
      .from('printer_telemetry')
      .select('*')
      .eq('user_id', authStore.userId)

    if (data) {
      const newMap = new Map<string, PrinterTelemetry>()
      for (const row of data) {
        newMap.set(row.printer_id, row as PrinterTelemetry)
      }
      telemetry.value = newMap
    }

    // Subscribe to real-time changes via Supabase (fallback, ~10s delay)
    telemetryChannel = supabase
      .channel('printer-telemetry-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'printer_telemetry',
          filter: `user_id=eq.${authStore.userId}`
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            const old = payload.old as { printer_id?: string }
            if (old.printer_id) {
              const newMap = new Map(telemetry.value)
              newMap.delete(old.printer_id)
              telemetry.value = newMap
            }
          } else {
            const row = payload.new as PrinterTelemetry
            if (row.printer_id) {
              updateTelemetryFromRow(row)
            }
          }
        }
      )
      .subscribe()

    // Connect to relay for instant updates from the Bridge
    connectRelay()
  }

  /** Apply a telemetry row update to the local state */
  function updateTelemetryFromRow(row: PrinterTelemetry) {
    const newMap = new Map(telemetry.value)
    newMap.set(row.printer_id, row)
    telemetry.value = newMap

    // Also sync the printer status from telemetry
    const idx = printers.value.findIndex(p => p.id === row.printer_id)
    if (idx !== -1) {
      const mappedStatus = mapGcodeToStatus(row.gcode_state, row.connected)
      if (printers.value[idx].status !== mappedStatus) {
        printers.value[idx] = { ...printers.value[idx], status: mappedStatus }
      }
    }
  }

  /** Connect to the PrintHQ relay server for real-time Bridge updates */
  async function connectRelay() {
    // Clean up previous listener
    if (relayCleanup) {
      relayCleanup()
      relayCleanup = null
    }

    const { supabase } = await import('@/services/supabase')
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) return

    // Connect the relay with current JWT
    relay.connect(session.access_token)

    // Handle incoming printer.status messages from the Bridge
    // The bridge now sends Supabase printer IDs, so matching is direct
    relayCleanup = relay.onMessage((msg) => {
      if (msg.type === 'printer.settings' && msg.printerId && msg.printSettingDefinitions) {
        printSettingDefs.value.set(msg.printerId, msg.printSettingDefinitions)
      }

      if (msg.type === 'printer.status' && msg.printerId && msg.data) {
        const state = msg.data
        const printerId = msg.printerId // This is the Supabase printer ID

        // Skip connection/disconnection events without full state
        if (state.event && !state.gcodeState) return

        // Merge bridge state into telemetry format
        const existing = telemetry.value.get(printerId)
        const updated: PrinterTelemetry = {
          ...(existing || { id: '', printer_id: printerId, user_id: '', created_at: '' }),
          printer_id: printerId,
          connected: state.connected ?? existing?.connected ?? false,
          platform: existing?.platform ?? null,
          nozzle_temp: state.nozzleTemp ?? existing?.nozzle_temp ?? 0,
          nozzle_target: state.nozzleTarget ?? existing?.nozzle_target ?? 0,
          bed_temp: state.bedTemp ?? existing?.bed_temp ?? 0,
          bed_target: state.bedTarget ?? existing?.bed_target ?? 0,
          chamber_temp: state.chamberTemp ?? existing?.chamber_temp ?? 0,
          part_fan: state.partFan ?? existing?.part_fan ?? 0,
          aux_fan: state.auxFan ?? existing?.aux_fan ?? 0,
          chamber_fan: state.chamberFan ?? existing?.chamber_fan ?? 0,
          gcode_state: state.gcodeState ?? existing?.gcode_state ?? 'UNKNOWN',
          print_percent: state.printPercent ?? existing?.print_percent ?? 0,
          remaining_time: state.remainingTime ?? existing?.remaining_time ?? 0,
          current_layer: state.currentLayer ?? existing?.current_layer ?? 0,
          total_layers: state.totalLayers ?? existing?.total_layers ?? 0,
          gcode_file: state.gcodeFile ?? existing?.gcode_file ?? '',
          subtask_name: state.subtaskName ?? existing?.subtask_name ?? '',
          speed_percent: state.speedPercent ?? existing?.speed_percent ?? 100,
          current_object: state.currentObject ?? existing?.current_object ?? '',
          excluded_objects: state.excludedObjects ?? existing?.excluded_objects ?? [],
          wifi_signal: state.wifiSignal ?? existing?.wifi_signal ?? '',
          light_on: state.lightOn ?? existing?.light_on ?? false,
          errors: state.errors ?? existing?.errors ?? [],
          filament_slots: existing?.filament_slots ?? [],
          updated_at: new Date().toISOString(),
          user_id: existing?.user_id ?? '',
        } as PrinterTelemetry

        updateTelemetryFromRow(updated)
      }

      // Handle print status changes from Bridge (completed/failed/cancelled)
      if (msg.type === 'print.statusChange' && msg.printId && msg.status) {
        import('./print').then(({ usePrintStore }) => {
          const printStore = usePrintStore()
          const print = printStore.getById(msg.printId)
          if (print) {
            const updates: Record<string, any> = {
              status: msg.status,
              progress: msg.progress ?? (msg.status === 'completed' ? 100 : print.progress),
            }
            if (msg.actualTime != null) updates.actual_time = msg.actualTime
            if (msg.actualFilament != null) updates.actual_filament = msg.actualFilament
            if (msg.status === 'completed' || msg.status === 'failed' || msg.status === 'cancelled') {
              updates.end_time = new Date().toISOString()
            }
            printStore.updatePrint(msg.printId, updates)
          } else {
            // Print not in local store yet — re-fetch
            printStore.fetchPrints()
          }
        }).catch(err => {
          console.warn('[printer] Failed to handle print.statusChange:', err)
        })
      }
    })

    // Refresh token when it changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token && relay.connected) {
        // Token refreshed — reconnect relay with new token
        relay.reconnect(session.access_token)
      }
    })

    // Store cleanup for auth listener
    const prevCleanup = relayCleanup
    relayCleanup = () => {
      prevCleanup?.()
      subscription.unsubscribe()
      relay.disconnect()
    }
  }

  const unsubscribeTelemetry = async () => {
    if (telemetryChannel) {
      const { supabase } = await import('@/services/supabase')
      supabase.removeChannel(telemetryChannel)
      telemetryChannel = null
    }
    if (relayCleanup) {
      relayCleanup()
      relayCleanup = null
    }
  }

  return {
    printers, loading, error, telemetry, slotAssignments, _loaded, _contextKey,
    activePrinters, printingPrinters, idlePrinters, printersNeedingMaintenance,
    printersByType, totalPrintTime, totalPrintCount, getById, getTelemetry,
    fetchPrinters, createPrinter, updatePrinter, deletePrinter,
    updateStatus, incrementPrintStats, recordMaintenance,
    subscribeTelemetry, unsubscribeTelemetry,
    // Material system & slots
    getSlotAssignments, fetchSlotAssignments, fetchAllSlotAssignments, updateMaterialSystem,
    assignSpoolToSlot, removeSpoolFromSlot, clearSlotAssignments,
    getPrintSettingDefinitions,
    clearData
  }
})

function mapGcodeToStatus(gcodeState: string, connected: boolean): PrinterStatus {
  if (!connected) return 'offline'
  switch (gcodeState) {
    case 'RUNNING':
    case 'PREPARE':
      return 'printing'
    case 'PAUSE':
      return 'paused'
    case 'FAILED':
      return 'error'
    case 'IDLE':
    case 'FINISH':
      return 'idle'
    default:
      return 'idle'
  }
}
